import { AxiosResponse } from 'axios';
import { axiosWithRetry } from 'utils/axios';
import { getCookieItem, getStorageItem, updateStorageItem } from 'utils/browserCache';

import { COOKIE_KEY, LOCAL_STORAGE_KEY, STORAGE_LOCATION } from 'constants/browserCache';
import { HTTP_REQUEST_METHODS } from 'constants/http';
import { PropelAuthType } from 'services/propel/types';
import CHAT_BACKEND from 'utils/chatBackend';
import { getChatContext } from 'utils/chatContext';
import { expired } from 'utils/token';

export type StartConversationRequest = {
  channel: string;
  userProfileId: string;
  origin: {
    intent?: string;
    serviceName: string;
    vendorId: string;
    vendorName: string;
  };
};

export type StartConversationResponse = {
  accessToken: string;
  anonymousRefreshToken: string;
  conversationId: string;
  twilioAccessToken: string;
  userProfileId: string;
};

interface StartConversationReturn extends PropelAuthType {
  retryCount: number | null;
}

const useCachedAuth = (cachedAuth: any) => {
  if (!cachedAuth) return false;
  if (!cachedAuth.isSplitPropelTwilioAuth) return false;
  if (!cachedAuth.conversationId) return false;
  if (!cachedAuth.userProfileId) return false;

  const isPropelTokenValid = !expired(cachedAuth.propel?.access_token);
  const isTwilioTokenValid = !expired(cachedAuth.twilio?.access_token);

  return isPropelTokenValid && isTwilioTokenValid;
};

export const startConversation = async (
  forceNewAuthTokens = false,
): Promise<StartConversationReturn> => {
  const url = `${process.env.CHAT_BACKEND_BASE_URL}/conversation/start`;

  const cachedAuth = getStorageItem(
    STORAGE_LOCATION.LOCAL_STORAGE,
    LOCAL_STORAGE_KEY.CVNA_PROPEL_CONVERSATION,
  );

  if (!forceNewAuthTokens && useCachedAuth(cachedAuth)) {
    return {
      retryCount: null,
      isCachedAuthValid: true,
      mojoAccessToken: cachedAuth.propel.access_token,
      anonymousRefreshToken: cachedAuth.anonymousRefreshToken,
      conversationId: cachedAuth.conversationId,
      twilioAccessToken: cachedAuth.twilio.access_token,
      userProfileId: cachedAuth.userProfileId,
    };
  }

  // Using getChatContext to ensure we have the latest, most up-to-date values.
  const as = getChatContext().authenticationState;
  const body: StartConversationRequest = CHAT_BACKEND.getStartConversationRequest(
    as.browserCookieId,
    as.carvanaDeviceId,
    as.user,
    as.userIdentifierType,
  );

  const idToken = getCookieItem(COOKIE_KEY.CVNA_ID_TOKEN);

  const headers = CHAT_BACKEND.getStartConversationHeaders(
    idToken,
    cachedAuth?.anonymousRefreshToken,
  );

  const res = await axiosWithRetry({
    method: HTTP_REQUEST_METHODS.POST,
    body,
    headers,
    url,
  });
  const retryCount = res?.config?.['axios-retry']?.retryCount ?? null;
  const { data }: AxiosResponse<StartConversationResponse> = res;

  const { accessToken, anonymousRefreshToken, conversationId, twilioAccessToken, userProfileId } =
    data;

  updateStorageItem(STORAGE_LOCATION.LOCAL_STORAGE, LOCAL_STORAGE_KEY.CVNA_PROPEL_CONVERSATION, {
    ...cachedAuth,
    isSplitPropelTwilioAuth: true, // FIXME - temp identification flag to align to latest convention.
    propel: {
      access_token: accessToken,
      user_profile_id: userProfileId,
      conversation_id: conversationId,

      // keeping these values to maintain same object structure
      ab_test_group: null,
      email_status: null,
      sms_status: null,
    },
    twilio: { access_token: twilioAccessToken },
    anonymousRefreshToken,
    userProfileId,
    conversationId,
  });

  return {
    mojoAccessToken: accessToken,
    anonymousRefreshToken,
    conversationId,
    twilioAccessToken,
    userProfileId,
    retryCount,
  };
};
